import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../state/authContext";

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const getSupabaseStorageKey = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) return null;
  try {
    const { hostname } = new URL(url);
    const ref = hostname.split(".")[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch {
    return null;
  }
};

const getCachedAccessToken = () => {
  if (typeof localStorage === "undefined") return null;
  const key = getSupabaseStorageKey();
  if (!key) return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.currentSession?.access_token || null;
  } catch {
    return null;
  }
};

const getCachedSessionUserRole = () => {
  if (typeof localStorage === "undefined") return null;
  const key = getSupabaseStorageKey();
  if (!key) return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const user = parsed?.user || parsed?.currentSession?.user || null;
    return user?.app_metadata?.role || user?.user_metadata?.role || null;
  } catch {
    return null;
  }
};

const normalizeRole = (value) => {
  if (!value || value === "authenticated") return null;
  return value;
};

/**
 * Simple role-based guard.
 *
 * - Accepts either a single `role` or an array of `roles`.
 * - Keeps a relaxed fallback for `admin` routes when role has not been resolved yet,
 *   but otherwise enforces explicit role membership.
 */
export default function RequireRole({ role: required, roles, children }) {
  const { user, role, loading, groupMemberships } = useAuth();
  const loc = useLocation();

  if (loading) return <div className="p-6">Laddar…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;

  const allowedRoles = (() => {
    if (Array.isArray(roles) && roles.length > 0) return roles;
    if (required) return [required];
    return [];
  })();

  const tokenPayload = decodeJwtPayload(getCachedAccessToken());
  const tokenRole =
    tokenPayload?.role ||
    tokenPayload?.app_metadata?.role ||
    tokenPayload?.user_metadata?.role ||
    null;
  const cachedUserRole = getCachedSessionUserRole();
  const effectiveRole =
    normalizeRole(role) ??
    normalizeRole(tokenRole) ??
    normalizeRole(cachedUserRole) ??
    normalizeRole(user?.app_metadata?.role) ??
    normalizeRole(user?.user_metadata?.role) ??
    null;

  const hasGroupAdminMembership = Boolean(
    user &&
      Array.isArray(groupMemberships) &&
      groupMemberships.some((m) => m.role_in_group === "group_admin")
  );
  const hasSellerMembership = Boolean(
    user &&
      Array.isArray(groupMemberships) &&
      groupMemberships.some((m) => m.role_in_group === "seller")
  );

  const allowed = (() => {
    if (!allowedRoles.length) return true;

    // Dev-unlock: allow any logged-in user for admin routes if role has not been resolved yet.
    if (!effectiveRole && allowedRoles.includes("admin")) return true;

    if (allowedRoles.includes("group_admin") && hasGroupAdminMembership) return true;
    if (allowedRoles.includes("seller") && hasSellerMembership) return true;

    return allowedRoles.includes(effectiveRole);
  })();

  if (!allowed) return <div className="p-6">Åtkomst nekad.</div>;
  return children;
}
